import type {SupabaseClient} from './supabaseTypes.js';
import {nudgeMentionEmails} from './mentionEmailApi.js';

export const COMMENT_CHANGE_EVENT = 'wcf-comment-change';

export type ImportedBreederNotes = Record<string, string[]>;

/** Historical Notes remain searchable/exportable from their Comments owner. */
export async function listImportedBreederNotes(
  sb: SupabaseClient,
  {signal}: {signal?: AbortSignal} = {},
): Promise<ImportedBreederNotes> {
  const request = sb.rpc('list_pig_breeder_imported_notes');
  const response = signal && typeof request.abortSignal === 'function' ? request.abortSignal(signal) : request;
  const {data, error} = await response;
  if (error) {
    // Source deployment is not permission to run the historical transfer.
    // Until migration 211 is approved/applied, the existing blob notes remain
    // the source. Never turn an authorization/network failure into empty data.
    if (
      (error.code === 'PGRST202' || error.code === '42883') &&
      String(error.message).includes('list_pig_breeder_imported_notes')
    ) {
      return Object.create(null) as ImportedBreederNotes;
    }
    throw new Error(`listImportedBreederNotes: ${error.message || String(error)}`);
  }
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error('listImportedBreederNotes: invalid notes response');
  }
  const result: ImportedBreederNotes = Object.create(null) as ImportedBreederNotes;
  for (const [id, notes] of Object.entries(data)) {
    if (!Array.isArray(notes) || notes.some((note) => typeof note !== 'string')) {
      throw new Error('listImportedBreederNotes: invalid note text');
    }
    result[id] = notes as string[];
  }
  return result;
}

// Comment payloads come back as jsonb built inside the mig-058 RPCs. Their key
// set lives in SQL and is not generated into TypeScript, so rows and results
// stay `unknown` here rather than being asserted into an invented interface —
// CommentsSection narrows the fields it renders itself.

export async function loadMentionableProfiles(
  sb: SupabaseClient,
  {signal}: {signal?: AbortSignal} = {},
): Promise<unknown[]> {
  if (!sb) return [];
  const request = sb.rpc('list_comment_mentionable_profiles');
  const response = signal && typeof request.abortSignal === 'function' ? request.abortSignal(signal) : request;
  const {data, error} = await response;
  if (error) return [];
  return data || [];
}

export async function listComments(
  sb: SupabaseClient,
  entityType: string,
  entityId: string,
  {limit = 50, signal}: {limit?: number; signal?: AbortSignal} = {},
): Promise<unknown[]> {
  if (!sb) return [];
  if (!entityType || !entityId) return [];
  const request = sb.rpc('list_comments', {
    p_entity_type: entityType,
    p_entity_id: entityId,
    p_limit: limit,
  });
  const response = signal && typeof request.abortSignal === 'function' ? request.abortSignal(signal) : request;
  const {data, error} = await response;
  if (error) throw new Error(`listComments: ${error.message || String(error)}`);
  return data || [];
}

// count_comments RETURNS int, and the runtime check below is a real narrowing
// rather than a claim — anything that is not a number settles to 0.
export async function countComments(sb: SupabaseClient, entityType: string, entityId: string): Promise<number> {
  if (!sb) return 0;
  if (!entityType || !entityId) return 0;
  const {data, error} = await sb.rpc('count_comments', {
    p_entity_type: entityType,
    p_entity_id: entityId,
  });
  if (error) return 0;
  return typeof data === 'number' ? data : 0;
}

// `mentions` are profile ids; `attachments` are the metadata rows produced by
// uploadCommentAttachment and stored verbatim as jsonb.
export type PostCommentInput = {
  entityType: string;
  entityId: string;
  body: string;
  entityLabel?: string | null;
  mentions?: string[];
  attachments?: unknown[];
};

export type EditCommentInput = {
  commentId: string;
  body: string;
  mentions?: string[];
  attachments?: unknown[];
};

export async function postComment(
  sb: SupabaseClient,
  {entityType, entityId, body, entityLabel, mentions = [], attachments = []}: PostCommentInput,
): Promise<unknown> {
  if (!sb) throw new Error('postComment: sb required');
  const {data, error} = await sb.rpc('post_comment', {
    p_entity_type: entityType,
    p_entity_id: entityId,
    p_body: body,
    p_entity_label: entityLabel || null,
    p_mentions: mentions,
    p_attachments: attachments,
  });
  if (error) throw new Error(`postComment: ${error.message || String(error)}`);
  // Mail the mentions promptly. Fire-and-forget by contract: the save has
  // already succeeded and the outbox owns delivery from here.
  nudgeMentionEmails(sb, mentions.length);
  window.dispatchEvent(new CustomEvent(COMMENT_CHANGE_EVENT));
  return data;
}

export async function editComment(
  sb: SupabaseClient,
  {commentId, body, mentions = [], attachments = []}: EditCommentInput,
): Promise<unknown> {
  if (!sb) throw new Error('editComment: sb required');
  const {data, error} = await sb.rpc('edit_comment', {
    p_comment_id: commentId,
    p_body: body,
    p_mentions: mentions,
    p_attachments: attachments,
  });
  if (error) throw new Error(`editComment: ${error.message || String(error)}`);
  // Only NEWLY added mentions produce a notification server-side, so an edit
  // that changed nothing queues nothing and this nudge finds no work.
  nudgeMentionEmails(sb, mentions.length);
  window.dispatchEvent(new CustomEvent(COMMENT_CHANGE_EVENT));
  return data;
}

export async function deleteComment(sb: SupabaseClient, commentId: string): Promise<unknown> {
  if (!sb) throw new Error('deleteComment: sb required');
  const {data, error} = await sb.rpc('delete_comment', {
    p_comment_id: commentId,
  });
  if (error) throw new Error(`deleteComment: ${error.message || String(error)}`);
  window.dispatchEvent(new CustomEvent(COMMENT_CHANGE_EVENT));
  return data;
}

export async function listCommentEdits(sb: SupabaseClient, commentId: string): Promise<unknown[]> {
  if (!sb) return [];
  const {data, error} = await sb.rpc('list_comment_edits', {
    p_comment_id: commentId,
  });
  if (error) throw new Error(`listCommentEdits: ${error.message || String(error)}`);
  return data || [];
}
